import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, Search } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';

interface TaskTransferModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (userId: string, userName: string, justification: string) => void;
  currentUserId: string;
  organizationId: string;
  isLoading?: boolean;
}

interface OrgMember {
  id: string;
  name: string;
  email: string;
}

export const TaskTransferModal: React.FC<TaskTransferModalProps> = ({
  open,
  onClose,
  onConfirm,
  currentUserId,
  organizationId,
  isLoading,
}) => {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<OrgMember | null>(null);
  const [justification, setJustification] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !organizationId) return;
    setLoading(true);
    supabase
      .from('user_organizations')
      .select('user_id, users:user_id(id, raw_user_meta_data)')
      .eq('organization_id', organizationId)
      .neq('user_id', currentUserId)
      .then(({ data }) => {
        const mapped: OrgMember[] = (data ?? [])
          .filter((row: any) => row.users)
          .map((row: any) => {
            const meta = row.users?.raw_user_meta_data ?? {};
            return {
              id: row.user_id,
              name: meta.full_name ?? meta.name ?? row.users?.email ?? row.user_id,
              email: row.users?.email ?? '',
            };
          });
        setMembers(mapped);
        setLoading(false);
      });
  }, [open, organizationId, currentUserId]);

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setJustification('');
      setSearch('');
    }
  }, [open]);

  const filtered = members.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase()),
  );

  const handleConfirm = useCallback(() => {
    if (!selectedUser || !justification.trim()) return;
    onConfirm(selectedUser.id, selectedUser.name, justification.trim());
  }, [selectedUser, justification, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md rounded border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-xl dark:bg-[var(--bg-elevated)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-5 py-3">
          <h3 className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] enterprise-text-primary">
            Transferir tarefa
          </h3>
          <button type="button" onClick={onClose} className="enterprise-text-muted hover:enterprise-text-primary">
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-3 px-5 py-4">
          {/* Search */}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-2.5 enterprise-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar membro..."
              className="w-full border border-[var(--border-default)] bg-[var(--bg-app)] py-2 pl-8 pr-3 text-[11px] enterprise-text-primary focus:border-blue-500 focus:outline-none"
              autoFocus
            />
          </div>

          {/* Members list */}
          <div className="max-h-[200px] overflow-y-auto rounded border border-[var(--border-default)]">
            {loading ? (
              <p className="px-3 py-4 text-center text-[10px] enterprise-text-muted">Carregando...</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-[10px] enterprise-text-muted">Nenhum membro encontrado</p>
            ) : (
              filtered.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedUser(m)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] transition ${
                    selectedUser?.id === m.id
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400'
                      : 'enterprise-text-primary hover:bg-[var(--bg-elevated)]'
                  }`}
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[9px] font-bold dark:bg-slate-700">
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[11px] font-medium">{m.name}</p>
                    <p className="truncate text-[9px] enterprise-text-muted">{m.email}</p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Justification */}
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Motivo da transferência (obrigatório)..."
            rows={2}
            className="w-full resize-none border border-[var(--border-default)] bg-[var(--bg-app)] px-3 py-2 text-[11px] enterprise-text-primary focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-[var(--border-default)] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-[var(--border-default)] px-4 py-1.5 text-[10px] font-medium enterprise-text-secondary hover:bg-[var(--bg-elevated)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedUser || !justification.trim() || isLoading}
            className="flex items-center gap-1.5 rounded bg-blue-600 px-4 py-1.5 text-[10px] font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowRight size={11} />
            {isLoading ? 'Transferindo...' : 'Transferir'}
          </button>
        </div>
      </div>
    </div>
  );
};
